// Structures de données optimisées

// Map avec expiration automatique
export class ExpiryMap<K, V> {
  private storage = new Map<K, {
    value: V;
    expiry: number;
  }>();

  constructor(private defaultTTL: number = 5 * 60 * 1000) {
    this.startCleanup();
  }

  set(key: K, value: V, ttl: number = this.defaultTTL): void {
    this.storage.set(key, {
      value,
      expiry: Date.now() + ttl
    });
  }

  get(key: K): V | undefined {
    const item = this.storage.get(key);
    if (!item) return undefined;

    if (Date.now() > item.expiry) {
      this.storage.delete(key);
      return undefined;
    }

    return item.value;
  }

  private startCleanup() {
    setInterval(() => {
      const now = Date.now();
      for (const [key, item] of this.storage.entries()) {
        if (now > item.expiry) {
          this.storage.delete(key);
        }
      }
    }, 60 * 1000); // Nettoyage toutes les minutes
  }
}

// File de priorité optimisée
export class PriorityQueue<T> {
  private heap: Array<{
    item: T;
    priority: number;
  }> = [];

  enqueue(item: T, priority: number): void {
    this.heap.push({ item, priority });
    this.bubbleUp(this.heap.length - 1);
  }

  dequeue(): T | undefined {
    if (this.heap.length === 0) return undefined;

    const result = this.heap[0].item;
    const last = this.heap.pop();
    
    if (this.heap.length > 0 && last) {
      this.heap[0] = last;
      this.bubbleDown(0);
    }

    return result;
  }

  private bubbleUp(index: number): void {
    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / 2);
      if (this.heap[parentIndex].priority >= this.heap[index].priority) break;
      
      [this.heap[parentIndex], this.heap[index]] = 
      [this.heap[index], this.heap[parentIndex]];
      
      index = parentIndex;
    }
  }

  private bubbleDown(index: number): void {
    while (true) {
      let highestPriority = index;
      const leftChild = 2 * index + 1;
      const rightChild = 2 * index + 2;

      if (leftChild < this.heap.length && 
          this.heap[leftChild].priority > this.heap[highestPriority].priority) {
        highestPriority = leftChild;
      }

      if (rightChild < this.heap.length && 
          this.heap[rightChild].priority > this.heap[highestPriority].priority) {
        highestPriority = rightChild;
      }

      if (highestPriority === index) break;

      [this.heap[index], this.heap[highestPriority]] = 
      [this.heap[highestPriority], this.heap[index]];
      
      index = highestPriority;
    }
  }
}